import type { PDFDocumentProxy } from "pdfjs-dist";

import { type RefObject, useCallback, useLayoutEffect, useRef, useState } from "react";

import type { PreviewFitMode } from "./previewFitMode";

import { fitZoom, measureFitBox, type PageSize } from "./fitZoom";
import { clampZoom } from "./zoom";

/** One page's size at scale 1, read from its viewport so rotation is included. */
async function readPageSize(pdf: PDFDocumentProxy, pageNumber: number): Promise<PageSize> {
  const viewport = (await pdf.getPage(pageNumber)).getViewport({ scale: 1 });
  return { width: viewport.width, height: viewport.height };
}

/** Every page's size, given the first's; a page that cannot be read keeps the first's. */
async function readPageSizes(pdf: PDFDocumentProxy, first: PageSize): Promise<PageSize[]> {
  const rest = await Promise.allSettled(
    Array.from({ length: pdf.numPages - 1 }, (_, i) => readPageSize(pdf, i + 2))
  );
  return [first, ...rest.map((page) => (page.status === "fulfilled" ? page.value : first))];
}

function initialZoom(
  defaultScale: number | PreviewFitMode | undefined,
  pageSizes: readonly PageSize[],
  viewport: HTMLElement | null
): number {
  if (typeof defaultScale === "number") return clampZoom(defaultScale);
  const [firstPage] = pageSizes;
  if (firstPage == null || viewport == null) return 1;
  return fitZoom(defaultScale ?? "auto", firstPage, pageSizes, measureFitBox(viewport));
}

interface LoadedPdf {
  fileUrl: string;
  pdf: PDFDocumentProxy;
  pageSizes: PageSize[];
  /** False while later pages still stand in at the first page's size. */
  sized: boolean;
}

/** Why a file could not be shown. */
export type PdfFailure = "unreadable" | "password";

/**
 * Loading a file, its page sizes and the zoom it opens at.
 *
 * Load results are kept with the file they belong to, so swapping files never
 * shows one document laid out with another's pages.
 */
export function usePdfDocument(
  fileUrl: string,
  defaultScale: number | PreviewFitMode | undefined,
  viewportRef: RefObject<HTMLDivElement | null>
) {
  const [loaded, setLoaded] = useState<LoadedPdf | null>(null);
  const [failure, setFailure] = useState<{ fileUrl: string; reason: PdfFailure } | null>(null);
  const [zoom, setZoom] = useState(1);

  // Dropped as soon as the file changes: react-pdf destroys the old document, so
  // coming back to a file must wait for its new load rather than show the last one.
  const [shownFile, setShownFile] = useState(fileUrl);
  if (shownFile !== fileUrl) {
    setShownFile(fileUrl);
    setLoaded(null);
    setFailure(null);
  }

  /**
   * Bumped whenever a file is switched to or a load starts, so only the latest
   * load lands — not a slower earlier one, even of the same file revisited.
   */
  const generationRef = useRef(0);
  // A layout effect, so the bump lands before any load for the new file reports.
  useLayoutEffect(() => {
    generationRef.current += 1;
  }, [fileUrl]);

  const onLoadSuccess = useCallback(
    (pdf: PDFDocumentProxy) => {
      generationRef.current += 1;
      const generation = generationRef.current;
      const isLatest = () => generationRef.current === generation;
      void readPageSize(pdf, 1).then(
        (first) => {
          if (!isLatest()) return;
          // Laid out at the first page's size at once; the rest arrive in one update.
          const provisional = Array.from({ length: pdf.numPages }, () => first);
          setZoom(initialZoom(defaultScale, provisional, viewportRef.current));
          setLoaded({ fileUrl, pdf, pageSizes: provisional, sized: pdf.numPages <= 1 });
          if (pdf.numPages <= 1) return;
          void readPageSizes(pdf, first).then((pageSizes) => {
            if (isLatest()) setLoaded({ fileUrl, pdf, pageSizes, sized: true });
          });
        },
        () => {
          if (isLatest()) setFailure({ fileUrl, reason: "unreadable" });
        }
      );
    },
    [fileUrl, defaultScale, viewportRef]
  );

  const onLoadError = useCallback(() => setFailure({ fileUrl, reason: "unreadable" }), [fileUrl]);
  // Never answered, so pdf.js does not fall back to prompting for it.
  const onPassword = useCallback(() => setFailure({ fileUrl, reason: "password" }), [fileUrl]);

  const current = loaded?.fileUrl === fileUrl ? loaded : null;

  const fit = useCallback(
    (mode: PreviewFitMode, pageIndex: number) => {
      const page = current?.pageSizes[pageIndex];
      const viewport = viewportRef.current;
      if (current == null || page == null || viewport == null) return;
      setZoom(fitZoom(mode, page, current.pageSizes, measureFitBox(viewport)));
    },
    [current, viewportRef]
  );

  return {
    pdf: current?.pdf ?? null,
    pageSizes: current?.pageSizes ?? null,
    /** True once every page has its own size, rather than the first page's. */
    sized: current?.sized ?? false,
    zoom,
    setZoom,
    fit,
    failure: failure?.fileUrl === fileUrl ? failure.reason : null,
    onLoadSuccess,
    onLoadError,
    onPassword,
  };
}
